<template>
  <div class="page-container">
    <Sidebar />
    <div class="main-content">
      <div class="page-header">
        <h1>知识库管理</h1>
        <div class="header-actions">
          <span class="count">共 {{ total }} 条记录</span>
          <button class="upload-btn" @click="showUploadModal = true">+ 添加知识</button>
        </div>
      </div>
      
      <div class="filter-bar">
        <div class="category-tabs">
          <button :class="['tab', { active: category === 'policy' }]" @click="category = 'policy'; subcategory = ''">政策法规</button>
          <button :class="['tab', { active: category === 'course' }]" @click="category = 'course'; subcategory = ''">专家授课</button>
          <button :class="['tab', { active: category === 'opinion' }]" @click="category = 'opinion'; subcategory = ''; fetchOpinionList()">大V观点</button>
          <button :class="['tab', { active: category === 'tech' }]" @click="category = 'tech'">技术资料</button>
        </div>
        <div class="subcategory-tabs" v-if="category === 'opinion'">
          <button :class="['subtab', { active: opinionSource === 'all' }]" @click="opinionSource = 'all'; fetchOpinionList()">全部</button>
          <button :class="['subtab', { active: opinionSource === '药谷' }]" @click="opinionSource = '药谷'; fetchOpinionList()">药谷</button>
          <button :class="['subtab', { active: opinionSource === '采药翁' }]" @click="opinionSource = '采药翁'; fetchOpinionList()">采药翁</button>
          <button :class="['subtab', { active: opinionSource === '赛柏蓝' }]" @click="opinionSource = '赛柏蓝'; fetchOpinionList()">赛柏蓝</button>
          <button :class="['subtab', { active: opinionSource === '猎药人' }]" @click="opinionSource = '猎药人'; fetchOpinionList()">猎药人</button>
        </div>
        <div class="subcategory-tabs" v-if="category === 'tech'">
          <button :class="['subtab', { active: subcategory === '' }]" @click="subcategory = ''">全部</button>
          <button :class="['subtab', { active: subcategory === 'production_standard' }]" @click="subcategory = 'production_standard'">规范化生产技术规程</button>
          <button :class="['subtab', { active: subcategory === 'national_standard' }]" @click="subcategory = 'national_standard'">国家标准</button>
          <button :class="['subtab', { active: subcategory === 'product_grade' }]" @click="subcategory = 'product_grade'">中药材商品规格等级</button>
          <button :class="['subtab', { active: subcategory === 'processing_standard' }]" @click="subcategory = 'processing_standard'">国家中药饮片炮制规范</button>
        </div>
        <div class="subcategory-tabs" v-if="category === 'course'">
          <button :class="['subtab', { active: subcategory === '' }]" @click="subcategory = ''">全部</button>
          <button :class="['subtab', { active: subcategory === 'courseware' }]" @click="subcategory = 'courseware'">课件分享</button>
          <button :class="['subtab', { active: subcategory === 'expert' }]" @click="subcategory = 'expert'">专家授课</button>
          <button :class="['subtab', { active: subcategory === 'blogger' }]" @click="subcategory = 'blogger'">博主视频</button>
        </div>
        <div class="year-filter-row" v-if="category === 'policy'">
          <select v-model="year" class="year-select" @change="fetchList">
            <option value="">全部年份</option>
            <option v-for="y in years" :key="y" :value="y">{{ y }}年</option>
          </select>
        </div>
        <input type="text" v-model="search" placeholder="搜索..." class="search-input" />
      </div>
      
      <!-- 大V观点表格 -->
      <div class="content-body" v-if="category === 'opinion'">
        <div class="loading" v-if="loading">加载中...</div>
        <div class="error" v-else-if="error">{{ error }}</div>
        <table class="data-table" v-else>
          <thead>
            <tr>
              <th>ID</th>
              <th>标题</th>
              <th>来源</th>
              <th>作者</th>
              <th>发布日期</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in filteredOpinionList" :key="item.id">
              <td>{{ item.id }}</td>
              <td class="name-cell">
                <a :href="item.url" target="_blank" class="article-link">{{ item.title }}</a>
              </td>
              <td><span class="source-tag">{{ item.source }}</span></td>
              <td>{{ item.author || '-' }}</td>
              <td>{{ item.publishDate }}</td>
              <td class="actions">
                <button class="edit-btn" @click="editOpinionItem(item)">编辑</button>
                <button class="delete-btn" @click="deleteOpinionItem(item)">删除</button>
              </td>
            </tr>
            <tr v-if="filteredOpinionList.length === 0">
              <td colspan="6" class="empty">暂无数据</td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- 其他分类表格 -->
      <div class="content-body" v-else>
        <div class="loading" v-if="loading">加载中...</div>
        <div class="error" v-else-if="error">{{ error }}</div>
        
        <table class="data-table" v-else>
          <thead>
            <tr>
              <th>ID</th>
              <th>文件名</th>
              <th>分类</th>
              <th>子分类</th>
              <th>格式</th>
              <th>大小</th>
              <th>观看</th>
              <th>下载</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in filteredList" :key="item.id">
              <td>{{ item.id }}</td>
              <td class="name-cell">{{ item.name }}</td>
              <td><span :class="['category-tag', item.category]">{{ getCategoryLabel(item.category) }}</span></td>
              <td><span class="subcategory-tag" v-if="item.subcategory">{{ getSubcategoryLabel(item.subcategory) }}</span></td>
              <td>{{ item.file_type }}</td>
              <td>{{ (item.subcategory === 'blogger' || item.subcategory === 'expert') ? (item.finder_user_name || '视频号') : formatSize(item.file_size) }}</td>
              <td>{{ item.views }}</td>
              <td>{{ item.downloads }}</td>
              <td class="actions">
                <button class="edit-btn" @click="editItem(item)">编辑</button>
                <button class="preview-btn" @click="previewFile(item)">预览</button>
                <button class="delete-btn" @click="deleteItem(item)">删除</button>
              </td>
            </tr>
          </tbody>
        </table>
        
        <div class="pagination" v-if="totalPage > 1">
          <button class="page-btn" :disabled="page <= 1" @click="changePage(page - 1)">上一页</button>
          <span class="page-info">第 {{ page }} / {{ totalPage }} 页（共 {{ total }} 条）</span>
          <button class="page-btn" :disabled="page >= totalPage" @click="changePage(page + 1)">下一页</button>
        </div>
      </div>
    </div>

    <div class="modal" v-if="showUploadModal">
      <div class="modal-content" v-if="category === 'opinion'">
        <h2>添加大V观点文章</h2>
        <form @submit.prevent="saveOpinion">
          <div class="form-group">
            <label>来源</label>
            <select v-model="opinionForm.source" required>
              <option value="药谷">药谷</option>
              <option value="采药翁">采药翁</option>
              <option value="赛柏蓝">赛柏蓝</option>
              <option value="猎药人">猎药人</option>
            </select>
          </div>
          <div class="form-group">
            <label>标题</label>
            <input type="text" v-model="opinionForm.title" required placeholder="文章标题" />
          </div>
          <div class="form-group">
            <label>链接</label>
            <input type="text" v-model="opinionForm.url" required placeholder="原文链接" />
          </div>
          <div class="form-group">
            <label>作者</label>
            <input type="text" v-model="opinionForm.author" placeholder="作者（可选）" />
          </div>
          <div class="form-group">
            <label>发布日期</label>
            <input type="date" v-model="opinionForm.publishDate" />
          </div>
          <div class="form-actions">
            <button type="button" class="cancel-btn" @click="closeUploadModal">取消</button>
            <button type="submit" class="submit-btn" :disabled="saving">{{ saving ? '保存中...' : '添加' }}</button>
          </div>
        </form>
      </div>
      <div class="modal-content" v-else>
        <h2>添加政策法规</h2>
        <form @submit.prevent="uploadFile">
          <div class="form-group">
            <label>文件名称</label>
            <input type="text" v-model="uploadForm.name" required placeholder="输入文件名称" />
          </div>
          <div class="form-group">
            <label>文件分类</label>
            <select v-model="uploadForm.category" required @change="uploadForm.subcategory = ''">
              <option value="policy">政策法规</option>
              <option value="course">专家授课</option>
              <option value="opinion">大V观点</option>
              <option value="tech">技术资料</option>
            </select>
          </div>
          <div class="form-group" v-if="uploadForm.category === 'tech'">
            <label>子分类</label>
            <select v-model="uploadForm.subcategory">
              <option value="">不选择</option>
              <option value="production_standard">规范化生产技术规程</option>
              <option value="national_standard">国家标准</option>
              <option value="product_grade">中药材商品规格等级</option>
              <option value="processing_standard">国家中药饮片炮制规范</option>
            </select>
          </div>
          <div class="form-group" v-if="uploadForm.category === 'course'">
            <label>子分类</label>
            <select v-model="uploadForm.subcategory">
              <option value="">不选择</option>
              <option value="courseware">课件分享</option>
              <option value="expert">专家授课</option>
              <option value="blogger">博主视频</option>
            </select>
          </div>
          <div class="form-group" v-if="uploadForm.subcategory === 'blogger' || uploadForm.subcategory === 'expert'">
            <label>视频号ID (finderUserName)</label>
            <input type="text" v-model="uploadForm.finder_user_name" placeholder="格式: sphxxx (如 sphWCAgAffcCDPW)" />
            <p class="field-tip">用于打开视频号主页，格式以 sph 开头</p>
          </div>
          <div class="form-group" v-if="uploadForm.subcategory === 'blogger' || uploadForm.subcategory === 'expert'">
            <label>视频ID (feedId)</label>
            <input type="text" v-model="uploadForm.feed_id" placeholder="格式: export/xxx (可选)" />
            <p class="field-tip">用于打开单条视频，格式以 export/ 开头，可选</p>
          </div>
          <div class="form-group" v-if="uploadForm.subcategory === 'blogger' || uploadForm.subcategory === 'expert'">
            <label>视频链接 (备用)</label>
            <input type="text" v-model="uploadForm.video_url" placeholder="其他平台视频链接（如B站/抖音）" />
            <p class="field-tip">非微信视频号链接填写此项</p>
          </div>
          <div class="form-group" v-if="uploadForm.subcategory !== 'blogger' && uploadForm.subcategory !== 'expert'">
            <label>选择文件（PDF/DOC/DOCX）</label>
            <div class="upload-area">
              <input type="file" accept=".pdf,.doc,.docx" @change="handleFileSelect" ref="fileInput" />
              <p class="upload-tip" v-if="!uploadForm.file">点击选择文件</p>
              <p class="file-selected" v-else>{{ uploadForm.file.name }}</p>
            </div>
          </div>
          <div class="form-actions">
            <button type="button" class="cancel-btn" @click="closeUploadModal">取消</button>
            <button type="submit" class="submit-btn" :disabled="uploading">{{ uploading ? '上传中...' : '上传' }}</button>
          </div>
        </form>
      </div>
    </div>

    <div class="modal" v-if="showEditModal">
      <div class="modal-content">
        <h2>编辑</h2>
        <form @submit.prevent="saveEdit">
          <div class="form-group">
            <label>名称</label>
            <input type="text" v-model="editForm.name" required />
          </div>
          <div class="form-group" v-if="editForm.subcategory === 'blogger' || editForm.subcategory === 'expert'">
            <label>视频号ID (finderUserName)</label>
            <input type="text" v-model="editForm.finder_user_name" placeholder="格式: sphxxx" />
            <p class="field-tip">用于打开视频号主页</p>
          </div>
          <div class="form-group" v-if="editForm.subcategory === 'blogger' || editForm.subcategory === 'expert'">
            <label>视频ID (feedId)</label>
            <input type="text" v-model="editForm.feed_id" placeholder="格式: export/xxx (可选)" />
            <p class="field-tip">用于打开单条视频，可选</p>
          </div>
          <div class="form-group" v-if="editForm.subcategory === 'blogger' || editForm.subcategory === 'expert'">
            <label>视频链接 (备用)</label>
            <input type="text" v-model="editForm.video_url" placeholder="其他平台视频链接" />
          </div>
          <div class="form-group">
            <label>作者</label>
            <input type="text" v-model="editForm.author" placeholder="作者" />
          </div>
          <div class="form-group">
            <label>描述</label>
            <textarea v-model="editForm.description" placeholder="描述"></textarea>
          </div>
          <div class="form-group" v-if="editForm.subcategory !== 'blogger' && editForm.subcategory !== 'expert' && editForm.subcategory !== 'courseware'">
            <label>发布日期</label>
            <input type="date" v-model="editForm.date" placeholder="YYYY-MM-DD" />
            <p class="field-tip">政策法规发布日期，用于年份筛选</p>
          </div>
          <div class="form-actions">
            <button type="button" class="cancel-btn" @click="closeEditModal">取消</button>
            <button type="submit" class="submit-btn" :disabled="saving">{{ saving ? '保存中...' : '保存' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Sidebar from '../components/Sidebar.vue'
import { getAdminPassword } from '../utils/api'

export default {
  name: 'PolicyFilesList',
  components: { Sidebar },
  data() {
    return {
      list: [],
      opinionList: [],
      total: 0,
      page: 1,
      pageSize: 15,
      loading: true,
      error: '',
      search: '',
      category: 'policy',
      subcategory: '',
      year: '',
      years: ['2026', '2025', '2024', '2023', '2022'],
      opinionSource: 'all',
      showUploadModal: false,
      uploadForm: { name: '', category: 'policy', subcategory: '', file: null, video_url: '', finder_user_name: '', feed_id: '' },
      uploading: false,
      showEditModal: false,
      editForm: { id: null, name: '', video_url: '', finder_user_name: '', feed_id: '', author: '', description: '', subcategory: '', date: '' },
      saving: false,
      showOpinionModal: false,
      opinionForm: { id: null, source: '药谷', title: '', url: '', author: '', publishDate: '' }
    }
  },
  computed: {
    totalPage() {
      return Math.ceil(this.total / this.pageSize)
    },
    filteredList() {
      let result = this.list
      if (this.category !== 'course') {
        result = result.filter(item => item.category === this.category)
      }
      if (this.subcategory) {
        result = result.filter(item => item.subcategory === this.subcategory)
      }
      if (this.search) {
        result = result.filter(item => 
          item.name.toLowerCase().includes(this.search.toLowerCase()) ||
          (item.filename && item.filename.toLowerCase().includes(this.search.toLowerCase()))
        )
      }
      return result
    },
    filteredOpinionList() {
      let result = this.opinionList
      if (this.opinionSource !== 'all') {
        result = result.filter(item => item.source === this.opinionSource)
      }
      if (this.search) {
        result = result.filter(item => 
          item.title.toLowerCase().includes(this.search.toLowerCase())
        )
      }
      return result
    }
  },
  mounted() { this.fetchList() },
  watch: {
    category(newVal) {
      this.page = 1
      if (newVal === 'opinion') {
        this.fetchOpinionList()
      } else {
        this.fetchList()
      }
    },
    subcategory() { this.page = 1; this.fetchList() },
    year() { this.page = 1; this.fetchList() }
  },
  methods: {
    async fetchList() {
      if (this.category === 'opinion') return
      this.loading = true
      try {
        const params = { page: this.page, pageSize: this.pageSize }
        if (this.category !== 'all') {
          params.category = this.category
        }
        if (this.subcategory) {
          params.subcategory = this.subcategory
        }
        if (this.year) {
          params.year = this.year
        }
        
        const res = await axios.get('/api/admin/policy-files', {
          params,
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (res.data.code === 200) {
          this.list = res.data.data?.list || []
          this.total = res.data.data?.total || 0
        }
      } catch (err) { this.error = '网络错误' }
      finally { this.loading = false }
    },
    changePage(p) {
      this.page = p
      this.fetchList()
    },
    async fetchOpinionList() {
      this.loading = true
      try {
        const params = {}
        if (this.opinionSource !== 'all') {
          params.source = this.opinionSource
        }
        const res = await axios.get('/api/expert-articles', {
          params,
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (res.data.code === 200) {
          this.opinionList = res.data.data || []
          this.total = this.opinionList.length
        }
      } catch (err) { this.error = '网络错误' }
      finally { this.loading = false }
    },
    getCategoryLabel(cat) {
      const labels = { policy: '政策法规', course: '专家授课', opinion: '大V观点', tech: '技术资料' }
      return labels[cat] || cat
    },
    getSubcategoryLabel(sub) {
      const labels = {
        production_standard: '规范化生产技术规程',
        national_standard: '国家标准',
        product_grade: '中药材商品规格等级',
        processing_standard: '国家中药饮片炮制规范',
        courseware: '课件分享',
        expert: '专家授课',
        blogger: '博主视频'
      }
      return labels[sub] || sub
    },
    formatSize(size) {
      if (!size) return '-'
      if (size < 1024) return size + 'B'
      if (size < 1024 * 1024) return (size / 1024).toFixed(1) + 'KB'
      return (size / 1024 / 1024).toFixed(1) + 'MB'
    },
    previewFile(item) {
      const url = item.file_url || item.video_url
      if (!url) {
        alert('暂无可预览内容')
        return
      }
      if (url.startsWith('http')) {
        window.open(url, '_blank')
      } else {
        window.open('https://yaotongbao.langya1616.com' + url, '_blank')
      }
    },
    async deleteItem(item) {
      if (!confirm('确定删除 ' + item.name + '？')) return
      try {
        if (item.category === 'courseware' || item.category === 'expert' || item.category === 'blogger') {
          const res = await axios.delete(`/api/admin/expert-courses/${item.id}`, {
            headers: { 'x-admin-password': getAdminPassword() }
          })
          if (res.data.code === 200) this.fetchList()
          else alert('删除失败')
        } else {
          const res = await axios.delete(`/api/admin/policy-files/${item.id}`, {
            headers: { 'x-admin-password': getAdminPassword() }
          })
          if (res.data.code === 200) this.fetchList()
          else alert('删除失败')
        }
      } catch (err) { alert('删除失败') }
    },
    handleFileSelect(e) {
      const file = e.target.files[0]
      if (!file) return
      if (!['.pdf', '.doc', '.docx'].some(ext => file.name.toLowerCase().endsWith(ext))) {
        alert('只支持PDF/DOC/DOCX格式')
        e.target.value = ''
        return
      }
      if (file.size > 50 * 1024 * 1024) {
        alert('文件大小不能超过50MB')
        e.target.value = ''
        return
      }
      this.uploadForm.file = file
    },
    closeUploadModal() {
      this.showUploadModal = false
      this.uploadForm = { name: '', category: 'policy', subcategory: '', file: null, video_url: '', finder_user_name: '', feed_id: '' }
      this.opinionForm = { id: null, source: '药谷', title: '', url: '', author: '', publishDate: '' }
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    closeOpinionModal() {
      this.showOpinionModal = false
      this.opinionForm = { id: null, source: '药谷', title: '', url: '', author: '', publishDate: '' }
    },
    editOpinionItem(item) {
      this.opinionForm = {
        id: item.id,
        source: item.source,
        title: item.title,
        url: item.url,
        author: item.author || '',
        publishDate: item.publishDate || ''
      }
      this.showUploadModal = true
    },
    async deleteOpinionItem(item) {
      if (!confirm('确定删除 ' + item.title + '？')) return
      try {
        const res = await axios.delete(`/api/expert-articles/${item.id}`, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (res.data.code === 200) this.fetchOpinionList()
        else alert('删除失败：' + res.data.msg)
      } catch (err) { alert('删除失败') }
    },
    async saveOpinion() {
      if (!this.opinionForm.title || !this.opinionForm.url || !this.opinionForm.source) {
        alert('请填写必填项')
        return
      }
      this.saving = true
      try {
        if (this.opinionForm.id) {
          // 编辑
          const res = await axios.put(`/api/expert-articles/${this.opinionForm.id}`, {
            source: this.opinionForm.source,
            title: this.opinionForm.title,
            url: this.opinionForm.url,
            author: this.opinionForm.author,
            publishDate: this.opinionForm.publishDate
          }, {
            headers: { 'x-admin-password': getAdminPassword() }
          })
          if (res.data.code === 200) {
            this.closeUploadModal()
            this.fetchOpinionList()
          } else {
            alert('保存失败：' + res.data.msg)
          }
        } else {
          // 新增
          const res = await axios.post('/api/expert-articles', {
            source: this.opinionForm.source,
            title: this.opinionForm.title,
            url: this.opinionForm.url,
            author: this.opinionForm.author,
            publishDate: this.opinionForm.publishDate || new Date().toISOString().split('T')[0]
          }, {
            headers: { 'x-admin-password': getAdminPassword() }
          })
          if (res.data.code === 200) {
            this.closeUploadModal()
            this.fetchOpinionList()
          } else {
            alert('添加失败：' + res.data.msg)
          }
        }
      } catch (err) { alert('操作失败') }
      finally { this.saving = false }
    },
    async uploadFile() {
      if (this.uploadForm.subcategory === 'blogger' || this.uploadForm.subcategory === 'expert') {
        if (!this.uploadForm.name) {
          alert('请输入视频名称')
          return
        }
        if (!this.uploadForm.finder_user_name && !this.uploadForm.video_url) {
          alert('请输入视频号ID或视频链接')
          return
        }
        this.uploading = true
        try {
          const res = await axios.post('/api/admin/expert-courses', {
            title: this.uploadForm.name,
            video_url: this.uploadForm.video_url,
            finder_user_name: this.uploadForm.finder_user_name,
            feed_id: this.uploadForm.feed_id,
            category: this.uploadForm.subcategory
          }, {
            headers: { 'x-admin-password': getAdminPassword() }
          })
          if (res.data.code === 200) {
            this.closeUploadModal()
            this.fetchList()
          } else {
            alert('添加失败：' + res.data.msg)
          }
        } catch (err) {
          alert('添加失败')
        } finally {
          this.uploading = false
        }
        return
      }
      
      if (!this.uploadForm.file) {
        alert('请选择文件')
        return
      }
      this.uploading = true
      try {
        const formData = new FormData()
        formData.append('file', this.uploadForm.file)
        formData.append('name', this.uploadForm.name || this.uploadForm.file.name.replace(/\.[^.]+$/, ''))
        formData.append('category', this.uploadForm.category)
        if (this.uploadForm.subcategory) {
          formData.append('subcategory', this.uploadForm.subcategory)
        }
        
        const res = await axios.post('/api/admin/policy-files', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-admin-password': getAdminPassword()
          }
        })
        if (res.data.code === 200) {
          this.closeUploadModal()
          this.fetchList()
        } else {
          alert('上传失败：' + res.data.msg)
        }
      } catch (err) {
        alert('上传失败')
      } finally {
        this.uploading = false
      }
    },
    editItem(item) {
      this.editForm = {
        id: item.id,
        name: item.name,
        video_url: item.file_url || item.video_url || '',
        finder_user_name: item.finder_user_name || '',
        feed_id: item.feed_id || '',
        author: item.author || '',
        description: item.description || '',
        subcategory: item.subcategory || '',
        date: item.date || ''
      }
      this.showEditModal = true
    },
    closeEditModal() {
      this.showEditModal = false
      this.editForm = { id: null, name: '', video_url: '', finder_user_name: '', feed_id: '', author: '', description: '', subcategory: '', date: '' }
    },
    async saveEdit() {
      this.saving = true
      try {
        if (this.editForm.subcategory === 'blogger' || this.editForm.subcategory === 'courseware' || this.editForm.subcategory === 'expert') {
          const res = await axios.put(`/api/admin/expert-courses/${this.editForm.id}`, {
            title: this.editForm.name,
            video_url: this.editForm.video_url,
            finder_user_name: this.editForm.finder_user_name,
            feed_id: this.editForm.feed_id,
            author: this.editForm.author,
            description: this.editForm.description
          }, {
            headers: { 'x-admin-password': getAdminPassword() }
          })
          if (res.data.code === 200) {
            this.closeEditModal()
            this.fetchList()
          } else {
            alert('保存失败：' + res.data.msg)
          }
        } else {
          const res = await axios.put(`/api/admin/policy-files/${this.editForm.id}`, {
            name: this.editForm.name,
            author: this.editForm.author,
            description: this.editForm.description,
            date: this.editForm.date
          }, {
            headers: { 'x-admin-password': getAdminPassword() }
          })
          if (res.data.code === 200) {
            this.closeEditModal()
            this.fetchList()
          } else {
            alert('保存失败：' + res.data.msg)
          }
        }
      } catch (err) {
        alert('保存失败')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.page-container { display: flex; height: 100vh; overflow: hidden; background: #f5f5f5; }
.main-content { flex: 1; padding: 30px; margin-left: 260px; overflow-y: auto; height: 100vh; }
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.page-header h1 { font-size: 24px; color: #2c3e50; }
.header-actions { display: flex; align-items: center; gap: 20px; }
.count { color: #666; font-size: 14px; }
.upload-btn { padding: 10px 20px; background: #27ae60; color: white; border: none; border-radius: 5px; cursor: pointer; }
.filter-bar { margin-bottom: 20px; }
.category-tabs { display: flex; gap: 5px; margin-bottom: 10px; }
.tab { padding: 8px 16px; background: #ecf0f1; border: none; border-radius: 5px; cursor: pointer; font-size: 13px; }
.tab.active { background: #3498db; color: white; }
.subcategory-tabs { display: flex; gap: 5px; margin-bottom: 10px; }
.subtab { padding: 6px 12px; background: #f8f9fa; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; color: #666; }
.subtab.active { background: #8e44ad; color: white; }
.search-input { width: 250px; padding: 10px 15px; border: 1px solid #ddd; border-radius: 5px; }
.year-filter-row { display: flex; gap: 5px; margin-bottom: 10px; align-items: center; }
.year-select { padding: 8px 12px; border: 1px solid #ddd; border-radius: 5px; background: #fff; font-size: 14px; cursor: pointer; }
.content-body { background: white; padding: 20px; border-radius: 10px; }
.loading, .error { text-align: center; padding: 50px; color: #666; }
.empty { text-align: center; padding: 30px; color: #999; }
.data-table { width: 100%; border-collapse: collapse; }
.data-table th, .data-table td { padding: 12px; border-bottom: 1px solid #eee; text-align: left; }
.data-table th { background: #f8f9fa; font-weight: 600; }
.name-cell { max-width: 250px; overflow: hidden; text-overflow: ellipsis; }
.article-link { color: #3498db; text-decoration: none; }
.article-link:hover { text-decoration: underline; }
.source-tag { padding: 4px 8px; border-radius: 4px; font-size: 12px; background: #e8f8f5; color: #16a085; }
.category-tag { padding: 4px 8px; border-radius: 4px; font-size: 12px; }
.category-tag.policy { background: #e8f4fd; color: #2980b9; }
.category-tag.course { background: #fef3e2; color: #d35400; }
.category-tag.opinion { background: #e8f8f5; color: #16a085; }
.category-tag.tech { background: #f4ecf7; color: #8e44ad; }
.subcategory-tag { padding: 3px 6px; border-radius: 3px; font-size: 11px; background: #e8e8e8; color: #555; }
.actions { display: flex; gap: 8px; }
.edit-btn, .preview-btn, .delete-btn { padding: 6px 12px; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; }
.edit-btn { background: #27ae60; color: white; }
.preview-btn { background: #3498db; color: white; }
.delete-btn { background: #e74c3c; color: white; }
.modal { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; justify-content: center; align-items: center; z-index: 1000; }
.modal-content { background: white; padding: 30px; border-radius: 10px; width: 400px; }
.modal-content h2 { margin: 0 0 20px; }
.form-group { margin-bottom: 15px; }
.form-group label { display: block; margin-bottom: 5px; color: #666; }
.form-group input[type="text"], .form-group input[type="date"], .form-group select, .form-group textarea { width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px; }
.form-group textarea { min-height: 60px; resize: vertical; }
.field-tip { font-size: 12px; color: #999; margin-top: 3px; }
.upload-area { border: 1px dashed #ddd; border-radius: 5px; padding: 20px; text-align: center; position: relative; }
.upload-area input[type="file"] { position: absolute; top: 0; left: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; }
.upload-tip, .file-selected { color: #666; font-size: 14px; }
.file-selected { color: #27ae60; }
.form-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
.cancel-btn, .submit-btn { padding: 10px 20px; border: none; border-radius: 5px; cursor: pointer; }
.cancel-btn { background: #ddd; }
.submit-btn { background: #27ae60; color: white; }
.submit-btn:disabled { background: #bdc3c7; cursor: not-allowed; }

.pagination { display: flex; justify-content: center; align-items: center; gap: 16px; margin-top: 20px; padding-top: 16px; border-top: 1px solid #eee; }
.page-btn { padding: 8px 20px; background: #3498db; color: #fff; border: none; border-radius: 5px; cursor: pointer; font-size: 13px; }
.page-btn:disabled { background: #bdc3c7; cursor: not-allowed; }
.page-info { font-size: 13px; color: #666; }
</style>